import { ChevronDown } from 'lucide-react'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import { Label } from '@/components/ui/label'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import { cn } from '@/lib/utils'
import { PRODUCT_WIDE_SCOPE } from '../grants'
import {
  CAREFORCE_SLUG,
  coversEveryOrg,
  isEveryOrg,
  listedOrgs,
  orgScopeLabel,
  toggleOrgScope,
} from '../orgScopes'

interface OrgScopeSelectProps {
  /** Capability name; disambiguates the checkbox labels across rows. */
  label: string
  /** `['*']`, or the slugs that each hold a row. */
  value: string[]
  /** Every org that could hold it, Careforce included. */
  orgs: string[]
  onChange: (next: string[]) => void
  disabled?: boolean
}

/**
 * Which organizations hold a capability, as one control per row.
 *
 * Careforce is shown ticked and locked rather than hidden: it holds every
 * capability, and a column that simply omitted it would read as though
 * unticking everything left the capability with nobody.
 */
export function OrgScopeSelect({
  label,
  value,
  orgs,
  onChange,
  disabled = false,
}: OrgScopeSelectProps) {
  const everyOrg = isEveryOrg(value)
  // An org holding the capability is listed even if the registry omits it:
  // one that is not listed cannot be unticked.
  const listed = listedOrgs(orgs, value)
  // What "everybody" means. Measured against the registry alone — `listed`
  // contains this row's own scope, so asking whether the scope covers it
  // answers itself.
  const registry = listedOrgs(orgs, [])
  // Careforce is in every scope, including one with no rows at all, which is
  // why it renders ticked on a capability no organization holds yet.
  const held = new Set([CAREFORCE_SLUG, ...(everyOrg ? listed : value)])
  // Ticked whenever the scope reaches every organization, however it says so.
  // Measured against the registry, not `listed`: that contains this row's own
  // scope, so asking whether the scope covers it answers itself.
  const reachesAll = coversEveryOrg([...held], registry)

  return (
    <Popover>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          disabled={disabled}
          // A fixed width, not `w-full`: the table measures content in
          // `table-layout: auto` before freezing the columns, and a
          // full-width trigger measures as wide as the row will allow —
          // which is what squeezed Capability and Permission.
          className="h-7 w-[132px] justify-between px-2 text-xs font-normal">
          <span className="truncate">{orgScopeLabel(value, registry)}</span>
          <ChevronDown size={12} className="ml-1 shrink-0 opacity-60" />
        </Button>
      </PopoverTrigger>
      <PopoverContent align="start" className="w-56 p-0">
        <div className="border-b px-2.5 py-2">
          <div className="flex items-center gap-2">
            <Checkbox
              id={`${label}::all-orgs`}
              checked={reachesAll}
              onCheckedChange={() =>
                onChange(toggleOrgScope(value, PRODUCT_WIDE_SCOPE, registry))
              }
            />
            <Label
              htmlFor={`${label}::all-orgs`}
              title={
                'One row serving every organization, including any added ' +
                'later. Off leaves Careforce, which holds everything anyway.'
              }
              className="cursor-pointer text-xs font-normal">
              All orgs
            </Label>
          </div>
        </div>
        <div className="max-h-56 overflow-y-auto">
          {listed.map((slug) => {
            const locked = slug === CAREFORCE_SLUG
            return (
              <div
                key={slug}
                className="flex items-center gap-2 px-2.5 py-1.5 hover:bg-muted/50">
                <Checkbox
                  id={`${label}::${slug}`}
                  checked={held.has(slug)}
                  disabled={locked}
                  onCheckedChange={() =>
                    onChange(toggleOrgScope(value, slug, listed))
                  }
                />
                <Label
                  htmlFor={`${label}::${slug}`}
                  title={
                    locked ? 'Careforce holds every capability.' : undefined
                  }
                  className={cn(
                    'text-xs font-normal',
                    locked ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
                  )}>
                  {slug}
                </Label>
              </div>
            )
          })}
        </div>
      </PopoverContent>
    </Popover>
  )
}
